import {
  useRouteMatch,
  Link,
  generatePath,
  useLocation,
} from 'react-router-dom';
import { fontWeightMedium } from 'utils/fontWeightConstants';
import { Card } from '@tackle-io/platform-ui';
import {
  makeStyles,
  Grid,
  LinearProgress,
  CircularProgress,
  Typography,
  useTheme,
} from 'vendor/material';
import { Alert as AlertIcon, ChevronDown, ChevronUp } from 'mdi-material-ui';
import PermissionTimeSubTitle from './PermissionTimeSubTitle';
import React from 'react';

const getFontColor = ({ isActive, isProgressComplete, disabled, theme }) => {
  if (disabled) {
    return theme.palette.NEUTRAL200;
  }

  if (isProgressComplete) {
    return theme.palette.GREEN400;
  }

  if (isActive) {
    return theme.palette.NEUTRAL600;
  }

  return theme.palette.BLUE400;
};

const useStyles = (
  isActive?: boolean,
  isProgressComplete?: boolean,
  error: boolean = false,
  disabled: boolean = true,
) =>
  makeStyles((theme) => {
    const fontColor = getFontColor({
      isActive,
      isProgressComplete,
      disabled,
      theme,
    });

    return {
      container: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
        position: 'relative',
      },
      wrapper: {
        position: 'relative',
        zIndex: 1,
      },
      link: {
        textDecoration: 'none',
        color: 'inherit',
      },
      icon: {
        color: error ? theme.palette.RED400 : fontColor,
      },
      header: {
        display: 'flex',
        flexDirection: 'row',
        padding: theme.spacing(3),
      },
      title: {
        fontWeight: fontWeightMedium,
        fontSize: theme.typography.pxToRem(18),
        lineHeight: theme.typography.pxToRem(24),
        color: fontColor,
      },
      alertContainer: {
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.RED400,
      },
      alertTitle: {
        fontWeight: fontWeightMedium,
        fontSize: theme.typography.pxToRem(18),
        lineHeight: theme.typography.pxToRem(24),
        color: theme.palette.RED400,
      },
      subTitle: {
        color: theme.palette.NEUTRAL300,
        fontSize: theme.typography.pxToRem(16),
        lineHeight: theme.typography.pxToRem(24),
        [theme.breakpoints.down('xs')]: {
          display: 'none',
        },
      },
      progress: {
        color: error
          ? theme.palette.RED400
          : isProgressComplete
          ? theme.palette.GREEN400
          : theme.palette.BLUE400,
      },
      docsToolTip: {
        alignSelf: 'flex-end',
        marginBottom: '2px',
        marginLeft: -theme.spacing(1),
      },
      progressRoot: {
        position: 'relative',
        display: 'flex',
        flexGrow: 1,
        justifyContent: 'flex-end',
      },
      progressBottom: {
        color: error ? theme.palette.RED400 : 'lightgrey',
      },
      progressTop: {
        color: error
          ? theme.palette.RED400
          : isProgressComplete
          ? theme.palette.GREEN400
          : theme.palette.BLUE400,
        position: 'absolute',
        right: 0,
      },
      contentContainer: {
        position: 'relative',
        boxShadow: `0 -1px 0 ${theme.palette.NEUTRAL040}`,
      },
      loadingBar: {
        position: 'absolute',
        top: -1,
        left: 0,
        right: 0,
      },
      chevron: {
        marginRight: theme.spacing(2),
      },
    };
  })();

const MsftSectionHeaderWrapper = ({
  path,
  app,
  slug,
  children,
  isActive,
}: {
  path: string;
  app: string;
  slug: string;
  children: React.ReactNode;
  isActive: boolean;
}) => {
  const classes = useStyles();
  const location = useLocation();

  if (!slug) {
    return <>{children}</>;
  }

  if (location.pathname.includes('/configuration')) {
    const basePath = location.pathname.split('/' + slug)[0];

    return (
      <Link
        className={classes.link}
        to={isActive ? basePath : `${basePath}/${slug}`}
      >
        {children}
      </Link>
    );
  }

  return (
    <Link
      className={classes.link}
      to={generatePath(path, {
        app,
        section: isActive ? null : slug,
      })}
    >
      {children}
    </Link>
  );
};

interface MsftCollapsibleFormSectionProps {
  children: React.ReactNode;
  icon?: JSX.Element;
  loading: boolean;
  slug: string;
  subTitle?: string | React.ReactNode;
  helpText?: string;
  docsToolTip?: React.ReactNode;
  permissions?: string;
  timeToComplete?: string;
  title: string;
  percentComplete: number;
  error?: boolean;
  disabled?: boolean;
}

function MsftCollapsibleFormSection({
  children,
  icon,
  loading,
  slug,
  subTitle,
  permissions,
  timeToComplete,
  helpText,
  docsToolTip,
  title,
  percentComplete,
  error = false,
  disabled = false,
}: MsftCollapsibleFormSectionProps) {
  const { path, params } = useRouteMatch<{ app?: string; section?: string }>();
  const location = useLocation();

  let isActive = false;

  if (location.pathname.includes('/configuration')) {
    isActive = location.pathname.endsWith('/' + slug);
  } else {
    isActive = params.section === slug;
  }

  const classes = useStyles(isActive, percentComplete === 100, error, disabled);
  const theme = useTheme();

  const disabledCardStyle = disabled
    ? { backgroundColor: theme.palette.NEUTRAL010 }
    : {};

  return (
    <div className={classes.container}>
      <div className={classes.wrapper}>
        <Card elevation={1} p={0} paperStyle={disabledCardStyle}>
          <MsftSectionHeaderWrapper
            path={path}
            app={params.app || ''}
            slug={slug}
            isActive={isActive}
          >
            <div className={classes.header}>
              <Grid container spacing={2}>
                {icon && (
                  <Grid item>
                    <div className={classes.icon}>{icon}</div>
                  </Grid>
                )}
                <Grid item xs container direction="column" spacing={1}>
                  <Grid item>
                    {!error && (
                      <Typography className={classes.title}>{title}</Typography>
                    )}
                    {error && (
                      <Grid item container spacing={1}>
                        <Grid item className={classes.alertContainer}>
                          <AlertIcon fontSize="inherit" />
                        </Grid>
                        <Grid item>
                          <Typography className={classes.alertTitle}>
                            {title}
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                  <Grid item className={classes.subTitle}>
                    {(subTitle || permissions || timeToComplete) && (
                      <PermissionTimeSubTitle
                        permissions={permissions}
                        timeToComplete={timeToComplete}
                        subTitle={subTitle}
                        helpText={helpText}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <div className={classes.docsToolTip}>{docsToolTip}</div>
              <div className={classes.progressRoot}>
                <Grid item>
                  {isActive ? (
                    <ChevronUp className={classes.chevron} />
                  ) : (
                    <ChevronDown className={classes.chevron} />
                  )}
                </Grid>
                <CircularProgress
                  variant={loading ? 'indeterminate' : 'determinate'}
                  size={20}
                  thickness={7}
                  value={100}
                  className={classes.progressBottom}
                  role={loading ? 'progressbar' : 'none'}
                />
                <CircularProgress
                  variant={loading ? 'indeterminate' : 'determinate'}
                  size={20}
                  thickness={7}
                  value={percentComplete}
                  className={classes.progressTop}
                  role={loading ? 'progressbar' : 'none'}
                />
              </div>
            </div>
          </MsftSectionHeaderWrapper>
          {isActive && (
            <div className={classes.contentContainer}>
              {loading && <LinearProgress className={classes.loadingBar} />}
              {children}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default MsftCollapsibleFormSection;
